import {
  createMirror,
  getLockValue,
  getShadowFabricInstance,
  isInGroup,
  removeObjectFromCanvas,
  removeRepeatingLinkParams
} from '@frontend/utils';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { fabric } from 'fabric';
import { getGroupNameByAddFieldId } from "@/common/preview/draw-smart-object";
import { FIELD_TYPE_SMART_OBJECT } from "@frontend/constants/type-fields-of-template";

const getScopePage = () => ShowPageScopeCollector.get(['getIsLightweight', 'canvas_data']);

export function drawUploadedImage({
   fields,
   data,
   canvas,
   shadows,
   id,
   group_fields,
   dimension,
   isApplyToAll
}) {
  removeObjectFromCanvas(canvas, 'upload_img_');
  const { canvas_data } = getScopePage();
  canvas_data[id].imgCoords = [];
  const is_lightweight = getScopePage().getIsLightweight();
  let index = 0;
  const promises = [];
  /* used to save bounding box info */
  const formData = new FormData();
  formData.append('type', 'Upload Image');

  fields.forEach((field) => {
    const options = JSON.parse(field.options);
    const groupName = getGroupNameByAddFieldId(
      String(field.id),
      canvas_data[id]
    );

    if (!(field.type === 'Upload Image' && (!group_fields || isInGroup(group_fields, field.name)))) return;

    const promise = new Promise((resolve) => {
      canvas_data[id].uploaded_image[field.element_id] = options;
      const element_id = field.element_id;
      let url = data[`${element_id}_saved`];
      const offset_x = parseFloat(data[`${element_id}_offset_x`]);
      const offset_y = parseFloat(data[`${element_id}_offset_y`]);
      const angle = parseFloat(data[`${element_id}_angle`]);
      const scale = parseFloat(data[`${element_id}_scale`]);
      let scale_y = parseFloat(data[`${element_id}_scale_y`]);
      const visible = !options['Hidden'];

      // patch for older layouts in which the vertical scale did not exist
      if (!scale_y && scale)
        scale_y = scale;

      // replace domain name from URL POR-2222
      if (url && url.includes('share?file=')) {
        url = removeRepeatingLinkParams(url, '&full_resolution');
      }

      if (url + is_lightweight === 'null') {
        resolve();
        return;
      }
      
      fabric.Image.fromURL(url, async function (oImg) {
        let w = parseInt(options.Width);
        let h = parseInt(options.Height);
        let r;
        h = (w * oImg.height) / oImg.width;
        r = h / parseInt(options.Height);
        r = r > 1 ? r : 1;
        w /= r;
        h /= r;
        
        if (options.Option1) {
          if (options.Option1.includes('auto_height')) {
            w = canvas_data[id].dimension.width;
            h = (canvas_data[id].dimension.width / oImg.width) * oImg.height;
          }
          
          if (options.Option1.includes('fix_y')) {
            oImg.set({ lockMovementX: false, lockMovementY: true });
          }
          
          if (options.Option1.includes('fix_x')) {
            oImg.set({ lockMovementX: true, lockMovementY: false });
          }
        }
        
        oImg.set({
          id: 'upload_img_' + index,
          groupName: groupName,
          element_id,
          order: parseInt(options['Order']),
          left: canvas_data[id].spacingFieldPosition[field.name]
            ? canvas_data[id].spacingFieldPosition[field.name].x
            : parseInt(options.X) + w / 2 + parseFloat(offset_x),
          top: parseInt(options.Y) + h / 2 + parseFloat(offset_y),
          // angle: parseInt(angle),
          originX: 'center',
          originY: 'center',
          selectable: options.Moveable == 'Yes',
          evented: options.Moveable == 'Yes',
          isApplyToAll,
          fieldId: options['id'],
          visible
        });
        
        oImg.scaleToWidth(w * scale);
        oImg.set({ scaleY: oImg.scaleY / scale * scale_y });
        oImg.set({ angle: parseFloat(angle) });

        let group_x = 0;
        let group_y = 0;
        
        if (groupName !== '' && canvas_data[id].smartObjCoords[groupName]) {
          group_x = parseInt(canvas_data[id].smartObjCoords[groupName]['X']);
          group_y = parseInt(canvas_data[id].smartObjCoords[groupName]['Y']);
        }
        
        oImg.set({
          left: oImg.left + group_x - dimension[2] + dimension[4].x,
          top: oImg.top + group_y - dimension[3] + dimension[4].y
        });

        const bShadow = +data?.[`${element_id}_shadow`];

        oImg.set({ shadow: getShadowFabricInstance(id, oImg.element_id, shadows, bShadow, oImg.scaleX, oImg.scaleY) });
        oImg.set({ locked: getLockValue(id, oImg.element_id) });
        
        if (!(url + is_lightweight)) {
          oImg.left = -1000;
          oImg.top = -1000;
          for (let prop in oImg) {
            if (isNaN(oImg[prop]) && typeof oImg[prop] === 'number') {
              oImg[prop] = 1;
            }
          }
        }
        
        canvas.add(oImg);

        /* update bounding box info */
        const bound = oImg.getBoundingRect();

        formData.append('element_ids[]', element_id);
        formData.append(element_id + '_width', bound.width);
        formData.append(element_id + '_height', bound.height);


        canvas_data[id].imgCoords.push({
          x: oImg.left - parseFloat(offset_x),
          y: oImg.top - parseFloat(offset_y),
          scaleX: oImg.scaleX / scale,
          scaleY: oImg.scaleY / scale_y,
        });
        
        index++;

        const isMirror = data[`${element_id}_is_mirror`] && data[`${element_id}_is_mirror`] === 'true';
        if (isMirror) {
          await createMirror(oImg, element_id, canvas, data)
            .then(() => {
              canvas.remove(oImg);
            });
        }
        resolve();
      });
    });

    promises.push(promise);
  });

  /* UPDATE BOUNDING BOX INFO */

  // comment for now as bounding box is returning NaN...
  // updateBoundingBoxInfo(formData, id);

  return Promise.all(promises);
}
